import React, { useState } from 'react'

import { FaqAccordion } from '@/components/Accordion/FaqAccordion'
import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { faqData } from '@/slices/HomePage/mocks/faq/data'
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import { Box, Container, InputAdornment, styled, TextField, Typography } from '@mui/material'

export const FaqPageSlice: React.FC = () => {
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const handleChange = (faqId: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedId(isExpanded ? faqId : null)
  }

  const filteredFaqData = faqData.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <StyledPageWrapper>
      <Container maxWidth="md">
        <StyledHeader>
          <StyledTitle variant="h1">
            Frequently Asked Questions
          </StyledTitle>
          <StyledSubtitle variant="body1">
            Find answers to common questions about PixieWorld personalized storybooks
          </StyledSubtitle>
        </StyledHeader>

        <StyledSearchBox>
          <TextField
            fullWidth
            placeholder="Search questions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon={faSearch} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </StyledSearchBox>

        <StyledFaqList>
          {filteredFaqData.length > 0 ? (
            filteredFaqData.map((faq) => (
              <FaqAccordion
                key={faq.id}
                question={faq.question}
                answer={faq.answer}
                expanded={expandedId === faq.id}
                onChange={handleChange(faq.id)}
              />
            ))
          ) : (
            <StyledNoResults>
              <Typography variant="body1">
                No questions found matching your search. Try different keywords.
              </Typography>
            </StyledNoResults>
          )}
        </StyledFaqList>

        <StyledFooterSection>
          <Typography variant="h6" gutterBottom>
            Still have questions?
          </Typography>
          <Typography variant="body1">
            Contact our support team at{' '}
            <StyledLink href="mailto:support@pixie.education">
              support@pixie.education
            </StyledLink>
          </Typography>
        </StyledFooterSection>
      </Container>
    </StyledPageWrapper>
  )
}

const StyledPageWrapper = styled(Box)(({ theme }) => ({
  paddingTop: pxToRem(40),
  paddingBottom: pxToRem(80),

  [theme.breakpoints.up('md')]: {
    paddingTop: pxToRem(60),
    paddingBottom: pxToRem(120),
  },
}))

const StyledHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: pxToRem(40),

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(60),
  },
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: pxToRem(16),
  fontSize: pxToRem(32),
  fontWeight: 700,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
    marginBottom: pxToRem(20),
  },
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: pxToRem(16),

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
  },
}))

const StyledSearchBox = styled(Box)(({ theme }) => ({
  marginBottom: pxToRem(32),

  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: pxToRem(12),

    '& fieldset': {
      borderColor: theme.palette.divider,
    },

    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },

    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },

  [theme.breakpoints.up('md')]: {
    marginBottom: pxToRem(48),
  },
}))

const StyledFaqList = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  marginBottom: pxToRem(60),

  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(2.5),
    marginBottom: pxToRem(80),
  },
}))

const StyledNoResults = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: pxToRem(40),
  color: theme.palette.primary.main,
}))

const StyledFooterSection = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: pxToRem(10),
  backgroundColor: theme.palette.background.paper,
  borderRadius: pxToRem(12),
  border: `1px solid ${theme.palette.divider}`,

  '& h6': {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },

  '& .MuiTypography-body1': {
    color: theme.palette.primary.main,
  },

  [theme.breakpoints.up('md')]: {
    padding: pxToRem(48),
  },
}))

const StyledLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 600,

  '&:hover': {
    textDecoration: 'underline',
  },
}))
